import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Container,
  Typography,
  TextField,
  Button,
  Box,
  Snackbar,
  Alert
} from '@mui/material';
import { useResetPassword } from 'src/api/user';
import { useTranslation } from 'react-i18next';
import { enqueueSnackbar } from 'src/components/snackbar';
import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';

const PasswordResetPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [error, setError] = useState<string | null>(null);
  const { resetPassword } = useResetPassword();
  const router = useRouter();
  const { t } = useTranslation();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== passwordConfirmation) {
      setError(t('password.passwords_dont_match'));
      return;
    }

    const searchParams = new URLSearchParams(location.search);
    const resetToken = searchParams.get('reset_password_token');
    if (!resetToken) {
      enqueueSnackbar(t('password_reset.no_reset_token'));
      setTimeout(() => router.push(paths.auth.jwt.login), 1000);
      return;
    }

    try {
      const response = await resetPassword(resetToken, password, passwordConfirmation);

      if (response.data.status === 'success') {
      enqueueSnackbar(t('password_reset.updated_successfully'));
        setTimeout(() => router.push(paths.auth.jwt.login), 1000);
      } else {
        setError(response.data.message || t('password_reset.error'));
      }
    } catch (err) {
      setError(t('password_reset.error'));
      console.error('Error resetting password:', err);
    }
  };

  const handleCloseSnackbar = () => {
    setError(null);
  };

  return (
    <>
      <Helmet>
        <title>{t('password_reset.title')}</title>
      </Helmet>
      <Container maxWidth="sm">
        <Box sx={{ my: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            {t('password_reset.reset_your_password')}
          </Typography>
          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              margin="normal"
              label={t('password.new_password')}
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <TextField
              fullWidth
              margin="normal"
              label={t('password_reset.confirm_new_password')}
              type="password"
              value={passwordConfirmation}
              onChange={(e) => setPasswordConfirmation(e.target.value)}
              required
            />
            <Button
              type="submit"
              variant="contained"
              color="primary"
              sx={{ mt: 3 }}
            >
              {t('password_reset.send')}
            </Button>
          </form>
        </Box>
      </Container>
      <Snackbar open={!!error} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
    </>
  );
};

export default PasswordResetPage;
